import { FC, useState } from "react";
import { ArrowDropDown } from "@mui/icons-material";
import { Button, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomNoRowsOverlay from "../../../features/results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import ValueChip, { ValueChipValue } from "./ValueChip";

interface Props {
	isLoading: boolean;
	title?: string;
	values: ValueChipValue[];
	elementsPerRow: number;
	valueVariant: "body1" | "h5";
	height?: number | string;
	areFilters?: boolean;
}

const MultiValueWidget: FC<Props> = ({
	isLoading,
	title,
	values,
	elementsPerRow,
	valueVariant,
	height = "100%",
	areFilters,
}) => {
	const { t } = useTranslation("feature.results");
	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	// Calcola larghezza per ogni chip
	const elemsFlexBasis = `calc(${100 / elementsPerRow}% - 8px)`;

	const titleTypography = title ? (
		<Typography variant="h5" mb=".6em">
			{title}
		</Typography>
	) : null;

	if (isLoading)
		return (
			<Stack direction="column" height={height}>
				{titleTypography}
				<Skeleton variant="rounded" height={height} width="100%" />
			</Stack>
		);

	if (values.length === 0)
		return (
			<Stack height={"100%"}>
				{titleTypography}{" "}
				<Paper elevation={3} sx={{ p: 2 }}>
					<CustomNoRowsOverlay />
				</Paper>
			</Stack>
		);

	const isExpandible = values.some((v) => v.reqExpand);

	const expansion = isExpandible ? (
		<>
			<Button
				variant="outlined"
				color="primary"
				sx={{ marginBottom: 2 }}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<Typography variant="body2" color="textPrimary" textTransform="capitalize">
					{isExpanded ? t("show less", { ns: "common" }) : t("show more", { ns: "common" })}
				</Typography>
				<ArrowDropDown sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
			</Button>
			<Stack direction="row" flexWrap="wrap" gap={1}>
				{values
					.filter((v) => v.reqExpand)
					.map((displayableValue, index) => (
						<ValueChip
							key={index}
							value={displayableValue}
							display={isExpanded ? "block" : "none"}
							flexBasis={elemsFlexBasis}
							areFilters={areFilters}
						/>
					))}
			</Stack>
		</>
	) : null;

	return (
		<Stack direction="column" height={height}>
			{titleTypography}
			<Paper elevation={3} sx={{ px: 2, pt: 2 }}>
				<Stack direction="row" flexWrap="wrap" gap={1}>
					{values
						.filter((v) => !v.reqExpand)
						.map((displayableValue, index) => (
							<ValueChip
								key={index}
								value={displayableValue}
								flexBasis={elemsFlexBasis}
								variant={valueVariant}
								areFilters={areFilters}
							/>
						))}
				</Stack>
				{expansion}
			</Paper>
		</Stack>
	);
};

export default MultiValueWidget;
